import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LazySection from '@/components/base/LazySection';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { refreshUserFromDB } from '@/lib/customAuth';

type StatusType = '全部' | '待发货' | '待收货' | '已完成';

const statusTabs: StatusType[] = ['全部', '待发货', '待收货', '已完成'];

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  '待付款': { bg: 'bg-gray-100', text: 'text-gray-500', label: '待付款' },
  '待发货': { bg: 'bg-amber-50', text: 'text-amber-500', label: '待发货' },
  '待收货': { bg: 'bg-sky-50', text: 'text-sky-500', label: '配送中' },
  '已完成': { bg: 'bg-emerald-50', text: 'text-emerald-600', label: '已完成' },
};

interface OrderItem {
  name: string;
  quantity: number;
  points: number;
  spec?: string;
  image?: string;
}

interface Order {
  id: string;
  order_no: string;
  items: OrderItem[];
  total_amount: number;
  status: string;
  created_at: string;
  address?: { name?: string; phone?: string; detail?: string } | null;
}

const OrdersPage = () => {
  const navigate = useNavigate();
  const { user, refresh: refreshAuth } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<StatusType>('全部');

  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [showConfirmSuccess, setShowConfirmSuccess] = useState(false);

  const fetchOrders = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (!error && data) setOrders(data as Order[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filtered = orders.filter((o) => {
    if (activeTab === '全部') return true;
    return o.status === activeTab;
  });

  const handleConfirmReceive = async () => {
    if (!confirmId || !user) return;
    setConfirming(true);

    const targetOrder = orders.find((o) => o.id === confirmId);
    const orderTotalPoints = Number(targetOrder?.total_amount ?? 0);

    const { error: orderError } = await supabase
      .from('orders')
      .update({ status: '已完成' })
      .eq('id', confirmId);

    if (!orderError) {
      setOrders((prev) => prev.map((o) => (o.id === confirmId ? { ...o, status: '已完成' } : o)));
      setConfirmId(null);
      setShowConfirmSuccess(true);
      await refreshUserFromDB();
      refreshAuth();
    }
    setConfirming(false);
  };

  const formatDate = (ts: string) =>
    new Date(ts).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });

  return (
    <div className="min-h-screen bg-gray-50 pb-20 max-w-[480px] mx-auto">
      {/* Header */}
      <LazySection animation="fadeIn" delay={0}>
        <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
          <div className="flex items-center px-4 h-14 gap-3">
            <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-arrow-left-line text-gray-700 text-xl" />
            </button>
            <h1 className="font-bold text-gray-900 text-base">我的订单</h1>
          </div>
          <div className="flex border-b border-gray-100 overflow-x-auto">
            {statusTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap px-2 ${activeTab === tab ? 'text-emerald-600 border-b-2 border-emerald-500' : 'text-gray-500'}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </header>
      </LazySection>

      <div className="px-4 pt-4 space-y-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-400">加载订单中...</p>
          </div>
        ) : filtered.length === 0 ? (
          <LazySection animation="fadeUp" delay={100}>
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <i className="ri-file-list-3-line text-5xl mb-3" />
              <p className="text-sm">暂无相关订单</p>
              <button onClick={() => navigate('/shop')} className="mt-4 bg-emerald-500 text-white px-6 py-2 rounded-full text-sm cursor-pointer whitespace-nowrap">
                去兑换
              </button>
            </div>
          </LazySection>
        ) : (
          filtered.map((order, idx) => {
            const cfg = statusConfig[order.status] || statusConfig['已完成'];
            return (
              <LazySection key={order.id} animation="fadeUp" delay={idx * 80}>
                <div className="bg-white rounded-2xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                    <span className="text-xs text-gray-400">订单号：{order.order_no}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                  </div>
                  <div className="px-4 py-3">
                    {order.items?.slice(0, 2).map((item, itemIdx) => (
                      <div key={itemIdx} className={`flex items-center gap-3 ${itemIdx > 0 ? 'mt-2.5 pt-2.5 border-t border-gray-50' : ''}`}>
                        <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                          <img src={item.image || ''} alt="" className="w-full h-full object-cover object-top" loading="lazy" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800 font-medium line-clamp-1 leading-snug">{item.name || '商品'}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{item.spec || '默认规格'}</p>
                          <div className="flex items-center justify-between mt-1">
                            <div>
                              <span className="text-emerald-600 font-semibold text-sm">{(item.points * item.quantity).toLocaleString()}</span>
                              <span className="text-emerald-600 text-xs ml-0.5">积分</span>
                            </div>
                            <span className="text-xs text-gray-400">x{item.quantity}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {order.items?.length > 2 && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                        <div className="flex -space-x-1">
                          {order.items.slice(2, 5).map((item, i) => (
                            <div key={i} className="w-5 h-5 rounded-full overflow-hidden border border-white bg-gray-100 flex-shrink-0">
                              {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                            </div>
                          ))}
                        </div>
                        <span>等共 {order.items.length} 件商品</span>
                      </div>
                    )}
                    <div className="mt-2.5 pt-2.5 border-t border-gray-50 flex items-center justify-between">
                      <span className="text-xs text-gray-400">共 {order.items?.reduce((s, i) => s + i.quantity, 0) || 1} 件</span>
                      <div>
                        <span className="text-xs text-gray-500">合计：</span>
                        <span className="text-emerald-600 font-bold text-sm">{order.total_amount.toLocaleString()}</span>
                        <span className="text-emerald-600 text-xs ml-0.5">积分</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-50 flex-wrap gap-2">
                    <span className="text-xs text-gray-400">{formatDate(order.created_at)}</span>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => navigate(`/order/${order.id}`)}
                        className="border border-gray-200 text-gray-600 text-xs px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap"
                      >
                        查看详情
                      </button>
                      {order.status === '待收货' && (
                        <button
                          onClick={() => setConfirmId(order.id)}
                          className="bg-emerald-500 text-white text-xs px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap"
                        >
                          确认收货
                        </button>
                      )}
                      {order.status === '已完成' && (
                        <button
                          onClick={() => navigate('/shop')}
                          className="border border-emerald-200 text-emerald-600 text-xs px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap"
                        >
                          再次兑换
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </LazySection>
            );
          })
        )}
      </div>

      {/* Confirm receive modal */}
      {confirmId && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setConfirmId(null)} />
          <div className="fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto bg-white rounded-t-2xl z-50 px-5 pt-6 pb-8">
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full mb-3">
                <i className="ri-checkbox-circle-line text-emerald-500 text-3xl" />
              </div>
              <h3 className="font-bold text-gray-900 text-base mb-1.5">确认收货</h3>
              <p className="text-sm text-gray-500 text-center leading-relaxed">
                请确认您已收到商品并检查无误<br />确认后订单将完成
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirmId(null)} className="flex-1 border border-gray-200 text-gray-600 py-3.5 rounded-full text-sm cursor-pointer whitespace-nowrap">
                再等等
              </button>
              <button
                onClick={handleConfirmReceive}
                disabled={confirming}
                className="flex-1 bg-emerald-500 text-white py-3.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {confirming ? '处理中...' : '确认收货'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Confirm Success Modal */}
      {showConfirmSuccess && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" />
          <div className="fixed inset-0 flex items-center justify-center z-50 px-8">
            <div className="bg-white rounded-3xl p-8 w-full flex flex-col items-center" style={{ animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
              <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
                <i className="ri-check-line text-emerald-500 text-4xl" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">收货成功！</h3>
              <p className="text-sm text-gray-500 text-center mb-6">感谢您的兑换，希望产品对您有帮助</p>
              <button
                onClick={() => setShowConfirmSuccess(false)}
                className="w-full bg-emerald-500 text-white py-3.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                好的
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes popIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default OrdersPage;
